/* Car Emergency vault — compact card.
 *
 * Both vaults auto-pull $50 on the 1st of every month from Spending.
 * Freely withdrawable (no goal-lock, no verification).
 */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { DepositButton, AutofillToggle, useAutofillRules } from "@/components/VaultControls";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldAlert, ArrowUpFromLine, X, CalendarClock,
  AlertTriangle, ShieldCheck, RotateCw, Lock, Unlock,
} from "lucide-react";

const SLUGS = { car_emergency: "car-emergency" };

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function CarVaultCard({ status, icon: Icon, tone, onAfterRetry, onWithdrawDone, autofill, onAutofill, onMsg }) {
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  if (!status || !status.applicable) return null;
  const bal = Number(status.balance || 0);
  const amount = Number(status.contribution_amount || 50);
  const threshold = Number(status.unlock_threshold || 1500);
  const unlocked = !!status.unlocked;
  const remaining = Number(status.remaining_to_unlock ?? Math.max(0, threshold - bal));
  const pct = threshold > 0 ? Math.min(100, Math.max(0, (bal / threshold) * 100)) : 0;
  const paid = status.paid_this_period;
  const slug = SLUGS[status.vault_type];
  const tid = status.vault_type.replace("_", "-");

  const retry = async () => {
    setRetryBusy(true);
    try {
      await api.post(`/auth/car/${slug}/retry`);
      if (onAfterRetry) await onAfterRetry();
    } catch { /* ignore */ }
    finally { setRetryBusy(false); }
  };

  return (
    <div className="bg-linen border border-sand rounded-xl p-5"
         data-testid={`${tid}-card`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${tone}`} strokeWidth={1.75} />
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage">
            {status.name}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
          unlocked ? "bg-forest text-white" : "bg-clay-light text-clay"
        }`} data-testid={`${tid}-lock-state`}>
          {unlocked
            ? <><Unlock className="w-2.5 h-2.5" strokeWidth={2} /> Unlocked</>
            : <><Lock className="w-2.5 h-2.5" strokeWidth={2} /> Locked until ${threshold.toLocaleString()}</>}
        </span>
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div className="font-mono text-3xl text-ink"
             data-testid={`${tid}-balance`}>
          {fmtMoney(bal)}
        </div>
        <div className="text-[10px] text-sage uppercase tracking-wider font-semibold text-right">
          $50 · every other payday<br/>
          <span className="text-moss font-mono normal-case tracking-normal">
            Next: {fmtDate(status.next_due)}
          </span>
        </div>
      </div>

      {/* Unlock progress */}
      <div className="mt-3">
        <div className="h-1.5 rounded-full bg-sand-warm overflow-hidden">
          <div className={`h-full ${unlocked ? "bg-forest" : "bg-gold"}`}
               style={{ width: `${pct}%` }}
               data-testid={`${tid}-unlock-progress`} />
        </div>
        <div className="mt-1 text-[10px] text-sage font-mono flex items-center justify-between">
          <span>{pct.toFixed(0)}% to unlock</span>
          <span>{unlocked
            ? "Free to withdraw"
            : `${fmtMoney(remaining)} to go`}</span>
        </div>
      </div>

      {/* Cycle-payment status pill */}
      <div className="mt-3">
        {paid ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-forest/12 text-forest"
                data-testid={`${tid}-status-paid`}>
            <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2} /> Paid this cycle
          </span>
        ) : status.period ? (
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-clay-light text-clay"
                  data-testid={`${tid}-status-due`}>
              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2} /> ${amount.toFixed(0)} due
            </span>
            <button onClick={retry} disabled={retryBusy}
              className="inline-flex items-center gap-1 text-[10px] text-forest hover:text-forest-deep font-medium disabled:opacity-60"
              data-testid={`${tid}-retry`}>
              <RotateCw className={`w-2.5 h-2.5 ${retryBusy ? "animate-spin" : ""}`} strokeWidth={2} />
              {retryBusy ? "Charging…" : "Retry now"}
            </button>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-sand-light text-moss"
                data-testid={`${tid}-status-scheduled`}>
            <CalendarClock className="w-2.5 h-2.5" strokeWidth={2} /> Starts {fmtDate(status.next_due)}
          </span>
        )}
      </div>

      {status.last_error && !paid && (
        <div className="mt-2 text-[10px] text-clay">
          Last try: {status.last_error}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <DepositButton vaultId={status.vault_id} vaultName={status.name} compact onMsg={onMsg} onDone={onWithdrawDone} />
        {unlocked ? (
          <button onClick={() => setShowWithdraw(true)} disabled={bal <= 0}
            className="flex-1 bg-transparent border border-ink text-ink rounded-md px-3 py-1.5 text-xs font-medium hover:bg-ink hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-1"
            data-testid={`${tid}-withdraw-btn`}>
            <ArrowUpFromLine className="w-3 h-3" strokeWidth={1.75} /> Withdraw
          </button>
        ) : (
          <button disabled
            title={`Locked until balance reaches $${threshold.toLocaleString()}`}
            className="flex-1 bg-transparent border border-sand text-sage rounded-md px-3 py-1.5 text-xs font-medium cursor-not-allowed inline-flex items-center justify-center gap-1"
            data-testid={`${tid}-withdraw-locked`}>
            <Lock className="w-3 h-3" strokeWidth={1.75} /> Locked until ${threshold.toLocaleString()}
          </button>
        )}
      </div>

      <div className="mt-3"><AutofillToggle vaultId={status.vault_id} rule={autofill} onChanged={onAutofill} onMsg={onMsg} compact /></div>

      {showWithdraw && (
        <WithdrawDialog status={status}
          onClose={() => setShowWithdraw(false)}
          onDone={() => { setShowWithdraw(false); onWithdrawDone && onWithdrawDone(); }} />
      )}
    </div>
  );
}

function WithdrawDialog({ status, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const slug = SLUGS[status.vault_type];
  const tid = status.vault_type.replace("_", "-");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/vaults/car/${slug}/withdraw`, { amount: parseFloat(amount) });
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid={`${tid}-withdraw-dialog`}>
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Withdraw</div>
          <button onClick={onClose} className="text-moss hover:text-ink"
                  data-testid={`${tid}-withdraw-close`}><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-2xl mt-3">{status.name}</h3>
        <p className="text-xs text-moss mt-2">Available: {fmtMoney(status.balance)}</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
            <input type="number" step="0.01" min="1" max={status.balance} required
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
              placeholder="0.00" data-testid={`${tid}-withdraw-amount`} autoFocus />
          </div>
          {err && <div className="text-sm text-clay" data-testid={`${tid}-withdraw-error`}>{err}</div>}
          <button type="submit" disabled={busy}
            className="w-full bg-gold text-white rounded-lg px-6 py-3 font-medium hover:bg-gold-deep disabled:opacity-60 transition-colors"
            data-testid={`${tid}-withdraw-submit`}>
            {busy ? "Releasing…" : "Release funds"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function CarVaultsSection() {
  const { user, refresh } = useAuth();
  const [statuses, setStatuses] = useState({ car_emergency: null });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const autofill = useAutofillRules();

  const load = useCallback(async () => {
    try {
      const b = await api.get("/vaults/car/car-emergency");
      setStatuses({ car_emergency: b.data });
    } catch { /* keep prior */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const afterRetry = async () => { await load(); if (refresh) await refresh(); };

  if (loading && !statuses.car_emergency) return null;
  const any = statuses.car_emergency?.applicable;
  if (!any) return null;

  return (
    <section className="mt-10" data-testid="car-vaults-section">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">On the road</div>
        <h2 className="font-serif text-2xl tracking-tight mt-1">Car Emergency</h2>
        <p className="text-xs text-moss mt-1 max-w-xl">
          $50 pulled from Spending every other payday for the day something breaks. <span className="text-clay font-medium">Stays locked until it grows past $1,500.</span>
        </p>
      </div>
      {msg && <div className="mb-3 px-3 py-2 rounded-lg bg-linen border border-sand text-xs text-ink" data-testid="car-vaults-msg">{msg}</div>}
      <div className="grid md:grid-cols-2 gap-3">
        <CarVaultCard status={statuses.car_emergency} icon={ShieldAlert}  tone="text-clay"
                      onAfterRetry={afterRetry} onWithdrawDone={load} onMsg={setMsg}
                      autofill={autofill.rules[statuses.car_emergency?.vault_id]} onAutofill={autofill.reload} />
      </div>
    </section>
  );
}
